/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */
import React, {
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

import {
  saveCalendarExercise,
  saveNonDateBasedWorkoutExercise,
  updateProgrammedCalendarItem,
  updateSavedWorkoutItem,
  swapProgrammedItemOnChildCalendar,
  setCombineWithInfo,
} from '../../ducks/calendarActions';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const alreadySavedGlobal = new Set();

const NoteTable = ({
  contentIndex,
  content,
  swapDropdownSelectedExercise,
  nonSwappedItemFromParentCalendar,
  date,
  dayNumber,
  isRecurring,
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const formatDate = () => `${moment(date).format('YYYY-MM-DD')}`;
  const combineWithInfo = useSelector((state) => state.calendar.data.combineWithInfo);
  const programType = useSelector((state) => state.calendar.data.programType);
  const handleAutoSaveCalendarNote = (contentIndexParam) => {
    const submissionObject = {
      /** Conditional key based on programType: "date" OR "dayNumber" */
      [programType === 'free-form-program' ? 'dayNumber' : 'date']:
        programType === 'free-form-program'
          ? dayNumber
          : moment(date).format('YYYY-MM-DD'),
      exerciseId: content.exerciseId,
      type: 'note',
      showSessionBreakItems: false,
      order: contentIndex,
    };
    if (content.description) {
      submissionObject.description = content.description;
    }
    if (content.groupingLetter) {
      submissionObject.groupingLetter = content.groupingLetter;
    }
    const formattedDate = moment(date).format('YYYY-MM-DD');
    if (programType === 'free-form-program') {
      dispatch(saveNonDateBasedWorkoutExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        dayNumber,
        () => {},
        {},
        content.assignedId,
      ));
    } else {
      dispatch(saveCalendarExercise(
        currentUser,
        contentIndexParam,
        submissionObject,
        formattedDate,
        () => {},
        {},
        content.assignedId,
      ));
    }
  };

  /** the CellMeasurer wrapped around each row in the virtualized list
   * can cause remounting of the item a few times as it measures
   * the "just in time" height, this can cause multiple firings of this
   * handleAutoSave funtion (which is a problem because it will create 3 of the same items in the data base).
   * To get around that we save the item by id
   * in the global set that will persist renders and if we have alreay saved
   * the item them we will not fire that function again.
   */

  useEffect(() => {
    if (content.preSave && !alreadySavedGlobal.has(content.assignedId)) {
      alreadySavedGlobal.add(content.assignedId);
      handleAutoSaveCalendarNote(contentIndex);
    }
  }, [content.assignedId]);

  /** This useEffect listens for changes is the exercise chosen from the
  * table dropdown menu
  */
  useEffect(() => {
    if (swapDropdownSelectedExercise) {
      const updatedItem = {
        assignedId: content.assignedId,
        date: programType === 'free-form-program' ? '' : formatDate(date),
        exerciseId: swapDropdownSelectedExercise.exerciseId,
        type: 'note',
        order: contentIndex,
      };
      if (programType === 'free-form-program') {
        updatedItem.dayNumber = dayNumber;
      }
      if (programType !== 'free-form-program') {
        updatedItem.isRecurring = isRecurring;
      }
      if (programType === 'free-form-program') {
        const reorder = false;
        dispatch(updateSavedWorkoutItem(
          currentUser.accountCode, updatedItem, contentIndex, dayNumber, reorder, {
            onSuccess: () => {},
            onError: () => {},
          },
        ));
      }
      if (programType === 'calendar') {
        if (nonSwappedItemFromParentCalendar) {
          const reorder = false;
          dispatch(swapProgrammedItemOnChildCalendar(
            currentUser.accountCode, updatedItem, contentIndex, formatDate(date), reorder, {
              onSuccess: () => {},
              onError: () => {},
            },
          ));
        } else {
          const reorder = false;
          dispatch(updateProgrammedCalendarItem(
            currentUser.accountCode, updatedItem, contentIndex, formatDate(date), reorder, {
              onSuccess: () => {},
              onError: () => {},
            },
          ));
        }
      }
    }
  }, [swapDropdownSelectedExercise, nonSwappedItemFromParentCalendar]);

  useEffect(() => {
    const isSource = combineWithInfo?.source?.length
      ? combineWithInfo.source.some((item) => item?.assignedId.toString() === content?.assignedId.toString())
      : false;

    const sourceItem = combineWithInfo?.source?.find(
      (item) => item?.assignedId.toString() === content?.assignedId.toString(),
    );

    const isTarget = combineWithInfo?.target?.assignedId === content?.assignedId;

    const targetItem = combineWithInfo?.target;
    /** Fresh, independent batchId, not combineWithInfo.batchId shared across
     * rows - the backend's dedup-by-batchId check for ASSIGNMENT_UPDATE is
     * scoped only by (batch_id, account), not by assignedId, so different
     * items sharing one batchId would silently merge into a single action
     * and lose each other's undo snapshots. */
    const batchId = uuidv4();

    if (!content.preSave && combineWithInfo && ((isSource) || (isTarget))) {
      dispatch(setCombineWithInfo(null));
      const updatedItem = {
        ...content,
        assignedId: content.assignedId,
        date: programType === 'free-form-program' ? '' : formatDate(date),
        exerciseId: content.exerciseId,
        groupingLetter: isSource
          ? sourceItem?.groupingLetter
          : isTarget
            ? targetItem?.groupingLetter
            : content?.groupingLetter,

        groupingColorCode: isSource
          ? sourceItem?.groupingColorCode
          : isTarget
            ? targetItem?.groupingColorCode
            : content?.groupingColorCode,
        type: 'note',
      };
      if (programType === 'free-form-program') {
        updatedItem.dayNumber = dayNumber;
      }
      if (programType !== 'free-form-program') {
        updatedItem.isRecurring = isRecurring;
      }
      if (programType === 'free-form-program') {
        // Only the combine target should trigger the position reorder.
        const reorder = isTarget;
        dispatch(updateSavedWorkoutItem(
          currentUser.accountCode, updatedItem, contentIndex, dayNumber, reorder, {
            onSuccess: () => {},
            onError: () => {},
          },
        ));
      }
      if (programType === 'calendar') {
        if (nonSwappedItemFromParentCalendar) {
          // Only the combine target should trigger the position reorder.
          const reorder = isTarget;
          dispatch(swapProgrammedItemOnChildCalendar(
            currentUser.accountCode, updatedItem, contentIndex, formatDate(date), reorder, {
              onSuccess: () => {},
              onError: () => {},
              batchId,
            },
          ));
        } else {
          // Only the combine target should trigger the position reorder.
          const reorder = isTarget;
          dispatch(updateProgrammedCalendarItem(
            currentUser.accountCode, updatedItem, contentIndex, formatDate(date), reorder, {
              onSuccess: () => {},
              onError: () => {},
              batchId,
            },
          ));
        }
      }
    }
  }, [combineWithInfo, nonSwappedItemFromParentCalendar]);

  return (
    <Container />
  );
};

NoteTable.propTypes = {
  contentIndex: PropTypes.number.isRequired,
  content: PropTypes.instanceOf(Object).isRequired,
  swapDropdownSelectedExercise: PropTypes.instanceOf(Object).isRequired,
  nonSwappedItemFromParentCalendar: PropTypes.bool.isRequired,
  date: PropTypes.string,
  dayNumber: PropTypes.string,
  isRecurring: PropTypes.string,
};

NoteTable.defaultProps = {
  date: null,
  dayNumber: null,
  isRecurring: null,
};

export default NoteTable;
